import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Share2, Bookmark } from "lucide-react";
import FoundingParentBadge from "@/components/site/FoundingParentBadge";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { promptLogin } from "@/lib/authRedirect";
import { toast } from "sonner";

/**
 * DiscussionCard — feed / list card.
 * Action row uses ONE icon family (lucide) with consistent stroke 1.75 & size 18px.
 * Bookmark toggle is fully wired: optimistic UI + server sync via POST /discussions/{id}/bookmark.
 * The like counter is a display only here (real toggle lives on DiscussionDetail).
 */
export default function DiscussionCard({ d, showRuangBadge = true, onBookmarkChanged, onLikeChanged }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [bookmarked, setBookmarked] = useState(!!d.viewer_bookmarked);
  const [pending, setPending] = useState(false);
  const [liked, setLiked] = useState(!!d.viewer_liked);
  const [likes, setLikes] = useState(d.likes || 0);
  const [likePending, setLikePending] = useState(false);
  // Burst counters — increment ONLY on inactive→active transitions so unliking or
  // unbookmarking doesn't fire the celebratory animation. Passing the counter as a
  // React `key` on the burst component remounts it → CSS animation restarts.
  const [likeBurst, setLikeBurst] = useState(0);
  const [bmBurst, setBmBurst] = useState(0);
  // `likeAnim` distinguishes the pop-up (activation) from the subtle dip-back
  // (deactivation). null = no animation currently playing.
  const [likeAnim, setLikeAnim] = useState(null); // "pop" | "unpop" | null

  // Keep local state in sync when parent re-fetches feed with new viewer flags.
  React.useEffect(() => {
    setBookmarked(!!d.viewer_bookmarked);
    setLiked(!!d.viewer_liked);
    setLikes(d.likes || 0);
  }, [d.discussion_id, d.viewer_bookmarked, d.viewer_liked, d.likes]);

  const toggleBookmark = async (e) => {
    // Prevent bubbling to any parent link/click handler AND cancel default anchor
    // navigation if this button is ever nested inside a Link (belt-and-suspenders).
    e.preventDefault();
    e.stopPropagation();
    if (!user) { promptLogin(nav); return; }
    if (pending) return;
    setPending(true);
    const prev = bookmarked;
    setBookmarked(!prev);
    if (!prev) setBmBurst((k) => k + 1); // celebrate on activation only
    try {
      // Backend `/discussions/{id}/bookmark` accepts both native `disc_*` and legacy
      // `thr_*` ids and routes to the correct canonical bookmark store.
      const r = await api.post(`/discussions/${d.discussion_id}/bookmark`);
      setBookmarked(!!r.data.bookmarked);
      onBookmarkChanged && onBookmarkChanged(d.discussion_id, !!r.data.bookmarked);
    } catch {
      setBookmarked(prev);
      toast.error("Gagal memperbarui bookmark.");
    } finally { setPending(false); }
  };

  const toggleLike = async (e) => {
    // Bug fix: previously Like was a <Link> that navigated to detail; now it toggles
    // inline (optimistic) and stops the event so no ancestor navigates either.
    e.preventDefault();
    e.stopPropagation();
    if (!user) { promptLogin(nav); return; }
    if (likePending) return;
    setLikePending(true);
    const prev = liked;
    const prevCount = likes;
    setLiked(!prev);
    setLikes(prevCount + (prev ? -1 : 1));
    if (!prev) {
      // Activation — Twitter-style: haptic tap + heart pop + particle burst.
      setLikeBurst((k) => k + 1);
      setLikeAnim("pop");
      try { navigator.vibrate && navigator.vibrate(12); } catch { /* Safari/desktop: not supported, silently skip */ }
    } else {
      // Deactivation — subtle dip, no particles, no haptic.
      setLikeAnim("unpop");
    }
    try {
      const r = await api.post(`/discussions/${d.discussion_id}/like`);
      setLiked(!!r.data.liked);
      setLikes(r.data.likes);
      onLikeChanged && onLikeChanged(d.discussion_id, { viewer_liked: !!r.data.liked, likes: r.data.likes });
    } catch {
      setLiked(prev);
      setLikes(prevCount);
      toast.error("Gagal memperbarui like.");
    } finally { setLikePending(false); }
  };

  const share = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const path = d.is_legacy ? `/forum/legacy/${d.discussion_id}` : `/forum/diskusi/${d.discussion_id}`;
    const url = `${window.location.origin}${path}`;
    try {
      if (navigator.share) await navigator.share({ title: d.title, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link disalin."); }
    } catch { /* user cancelled */ }
  };

  // Comment button remains a Link (tap → open detail to reply) but stopPropagation-guarded
  // so a future ancestor click handler cannot swallow the navigation.
  const onCommentNavClick = (e) => e.stopPropagation();

  // Server may include an explicit `link_url` (both native + legacy items carry it now).
  // Fallback to native diskusi route so older cached payloads still work.
  const detailHref = d.link_url || `/forum/diskusi/${d.discussion_id}`;

  return (
    <article className="rounded-2xl bg-white border border-brand-border p-5 shadow-sm" data-testid={`discussion-card-${d.discussion_id}`}>
      {/* Title moved to top per approved mockup (Feb 2026). Identity row follows below. */}
      <h3 className="font-sans text-base font-bold text-[#1C1C1F] leading-[1.3] mb-2">
        <Link to={detailHref} className="hover:underline underline-offset-2" data-testid={`card-title-link-${d.discussion_id}`}>
          {d.title}
        </Link>
      </h3>

      {/* Identity row: Avatar + Name + Ruang Badge + activity-aware Timestamp. Wraps on mobile. */}
      <header className="flex items-center gap-2 flex-wrap mb-3">
        <Link to={detailHref} className="flex items-center gap-2 min-w-0 group" data-testid={`card-author-link-${d.discussion_id}`}>
          <Avatar name={d.user_name} picture={d.user_picture} size="sm" />
          <span className="font-semibold text-sm text-[#1C1C1F] truncate group-hover:underline underline-offset-2 max-w-[160px]">{d.user_name || "Anonim"}</span>
          {d.is_founding_parent && <FoundingParentBadge size="xs" />}
        </Link>
        {showRuangBadge && d.ruang_slug && <RuangBadge d={d} />}
        {(() => {
          const cc = d.comment_count ?? d.reply_count ?? 0;
          const activityAt = d.last_activity || d.created_at;
          const verb = cc > 0 ? "dibalas" : "ditanya";
          return (
            <span
              className="text-xs text-[#8B8B90] whitespace-nowrap shrink-0"
              title={new Date(activityAt).toLocaleString("id-ID")}
              data-testid={`card-timestamp-${d.discussion_id}`}
            >
              · {verb} {timeAgo(activityAt)}
            </span>
          );
        })()}
      </header>

      {/* Body excerpt — click navigates to detail. */}
      {d.body_excerpt && (
        <Link to={detailHref} className="block mb-3" data-testid={`card-excerpt-link-${d.discussion_id}`}>
          <p className="text-sm text-[#4A4A4F] leading-relaxed">{d.body_excerpt}</p>
        </Link>
      )}

      {/* Action row — all buttons: e.preventDefault() + e.stopPropagation() so no click
          escapes to a parent link/handler and no icon accidentally navigates. */}
      <footer className="flex items-center gap-1 -ml-2">
        <ActionIcon
          testid={`card-like-${d.discussion_id}`}
          onClick={toggleLike}
          icon={
            <span className="relative inline-flex">
              <span
                key={`h-${likeBurst}-${likeAnim}-${liked}`}
                className={likeAnim === "pop" ? "tsr-heart-pop" : likeAnim === "unpop" ? "tsr-heart-unpop" : ""}
                // Inline color + transition:none bypass the parent button's `transition-colors`
                // so the fill flips from gray → coral instantly, matching Twitter's snap.
                style={{ color: liked ? "#C15C3F" : "#8B8B90", transition: "none" }}
                onAnimationEnd={() => setLikeAnim(null)}
              >
                <Heart className={`w-[18px] h-[18px] ${liked ? "fill-current" : ""}`} strokeWidth={1.75} />
              </span>
              {likeBurst > 0 && <BurstParticles key={`lb-${likeBurst}`} colors={["#C15C3F", "#F5A623"]} count={7} radius={24} />}
            </span>
          }
          count={likes}
          active={liked}
          activeClass="text-[#C15C3F]"
          label={liked ? "Batal suka" : "Suka"}
        />
        <ActionIcon
          testid={`card-comment-${d.discussion_id}`}
          as={Link} to={detailHref}
          onClick={onCommentNavClick}
          icon={<MessageCircle className="w-[18px] h-[18px]" strokeWidth={1.75} />}
          count={d.comment_count || 0}
          label="Komentar"
        />
        <ActionIcon
          testid={`card-share-${d.discussion_id}`}
          onClick={share}
          icon={<Share2 className="w-[18px] h-[18px]" strokeWidth={1.75} />}
          label="Bagikan"
        />
        <div className="flex-1" />
        <ActionIcon
          testid={`card-bookmark-${d.discussion_id}`}
          onClick={toggleBookmark}
          icon={
            <span className="relative inline-flex">
              <span key={`b-${bmBurst}-${bookmarked}`} className={bookmarked && bmBurst > 0 ? "tsr-bounce-pop" : ""}>
                <Bookmark className={`w-[18px] h-[18px] ${bookmarked ? "fill-current" : ""}`} strokeWidth={1.75} />
              </span>
              {bmBurst > 0 && <BurstParticles key={`bb-${bmBurst}`} color="#14294D" />}
            </span>
          }
          active={bookmarked}
          activeClass="text-[#C15C3F]"
          label={bookmarked ? "Tersimpan" : "Simpan"}
        />
      </footer>
    </article>
  );
}

/** Consistent icon-button. Wraps either <button> or a link component (`as` prop). */
function ActionIcon({ testid, icon, count, active, activeClass = "text-[#C15C3F]", label, onClick, as: As = "button", to, ...rest }) {
  const cls = `inline-flex items-center gap-1.5 h-9 px-2.5 rounded-full text-xs transition-colors hover:bg-brand-muted ${active ? activeClass + " font-semibold" : "text-[#8B8B90]"}`;
  const content = (
    <>
      {icon}
      {typeof count === "number" && <span>{count}</span>}
    </>
  );
  if (As === "button") {
    return (
      <button type="button" onClick={onClick} data-testid={testid} aria-label={label} className={cls}>
        {content}
      </button>
    );
  }
  return (
    <As to={to} data-testid={testid} aria-label={label} className={cls} {...rest}>
      {content}
    </As>
  );
}

/** RuangBadge — shows the Ruang icon + name; when the feed item carries `is_joined=false`
 * AND we're rendering in a discovery context, also exposes a coral "Gabung" quick-join
 * split-button that optimistically joins the Ruang without navigating away. */
function RuangBadge({ d }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [joined, setJoined] = React.useState(d.is_joined !== false); // treat undefined as joined (hide button)
  const [pending, setPending] = React.useState(false);
  // Track the last discussion id so state resets when the same card component is
  // reused for a different row (e.g. tab switch with virtualized reuse).
  React.useEffect(() => { setJoined(d.is_joined !== false); }, [d.discussion_id, d.is_joined]);

  const quickJoin = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { promptLogin(nav); return; }
    if (pending || joined) return;
    setPending(true);
    setJoined(true); // optimistic
    // Fire analytics event per §8.
    try { window.dataLayer && window.dataLayer.push({ event: "quick_join_click", ruang_slug: d.ruang_slug }); } catch { /* noop */ }
    try {
      await api.post(`/ruang/${d.ruang_id || d.ruang_slug}/join`);
    } catch {
      setJoined(false);
      toast.error("Gagal gabung Ruang. Coba lagi.");
    } finally { setPending(false); }
  };

  const showJoin = d.is_joined === false; // only show button when server explicitly said not-joined
  return (
    <span className="inline-flex items-stretch rounded-full bg-[#EEF2F8] text-brand-primary text-xs font-medium overflow-hidden max-w-full">
      <Link
        to={`/forum/ruang/${d.ruang_slug}`}
        className="inline-flex items-center gap-1 px-2.5 py-1 hover:bg-brand-primary/10 min-w-0"
        data-testid={`discussion-ruang-badge-${d.discussion_id}`}
      >
        <span aria-hidden className="shrink-0">{d.ruang_icon}</span>
        <span className="truncate">{d.ruang_name}</span>
      </Link>
      {showJoin && (
        joined ? (
          <span className="inline-flex items-center px-2 border-l border-[#c9d4e4] text-[#2E7D5B] font-semibold" data-testid={`discussion-ruang-joined-${d.discussion_id}`}>
            ✓
          </span>
        ) : (
          <button
            onClick={quickJoin}
            disabled={pending}
            data-testid={`discussion-ruang-join-${d.discussion_id}`}
            className="inline-flex items-center px-2.5 border-l border-[#c9d4e4] text-[#C15C3F] font-semibold hover:bg-[#C15C3F]/10 disabled:opacity-50"
          >
            Gabung
          </button>
        )
      )}
    </span>
  );
}

/** BurstParticles — small dots that radiate outward from the icon center and fade
 *  within 400ms. Absolutely positioned inside a `position: relative` parent, so
 *  zero layout impact. Remount via `key` prop to replay per activation.
 *  Accepts either a single `color` (legacy) or a `colors[]` list — particles
 *  alternate through the list, producing the coral+yellow mix on Like. */
function BurstParticles({ color, colors, count = 6, radius = 12 }) {
  const particles = React.useMemo(() => {
    // Compute palette inside the memo so its identity doesn't change every
    // render (satisfies react-hooks/exhaustive-deps at error level).
    const palette = colors && colors.length ? colors : [color || "#C15C3F"];
    return Array.from({ length: count }, (_, i) => {
      // Slight angular offset so no particle sits perfectly horizontal → livelier read.
      const angle = (i / count) * Math.PI * 2 + Math.PI / (count * 2);
      // Vary distance ±15% around the base radius so the burst feels organic, not stamped.
      const dist = radius * (0.85 + ((i * 37) % 30) / 100);
      return { tx: Math.cos(angle) * dist, ty: Math.sin(angle) * dist, color: palette[i % palette.length] };
    });
  }, [count, radius, color, colors]);
  return (
    <span aria-hidden className="absolute inset-0 pointer-events-none">
      {particles.map((p, i) => (
        <span
          key={i}
          className="tsr-burst-particle"
          style={{ backgroundColor: p.color, "--tsr-tx": `${p.tx}px`, "--tsr-ty": `${p.ty}px` }}
        />
      ))}
    </span>
  );
}

function Avatar({ name, picture }) {
  if (picture) {
    return <img src={picture} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />;
  }
  const letter = (name || "?").trim().charAt(0).toUpperCase();
  const palette = ["bg-rose-100 text-rose-700", "bg-amber-100 text-amber-700", "bg-emerald-100 text-emerald-700", "bg-sky-100 text-sky-700", "bg-violet-100 text-violet-700", "bg-orange-100 text-orange-700"];
  const idx = letter.charCodeAt(0) % palette.length;
  return (
    <span className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${palette[idx]}`}>
      {letter}
    </span>
  );
}

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!then) return "";
  const diffMs = Date.now() - then;
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return "baru saja";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} menit lalu`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} jam lalu`;
  const d = Math.floor(h / 24);
  if (d < 7) return d === 1 ? "kemarin" : `${d} hari lalu`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w} minggu lalu`;
  return new Date(iso).toLocaleDateString("id-ID", { day: "numeric", month: "short" });
}

export { timeAgo };

